import { useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import useIsMobile from 'hooks/useIsMobile'
import NotAvailableOnMobile from 'screens/NotAvailableOnMobile'

/**
 * Blocks the desktop-only surfaces on a mobile viewport.
 *
 * Used as a PATHLESS layout route wrapping the gated routes, rather than as a
 * per-route wrapper:
 *
 *   <Route element={<DesktopOnlyOutlet />}>
 *     ...gated routes...
 *   </Route>
 *
 * A pathless parent contributes nothing to react-router's ranking, so match
 * precedence is unchanged and absolute child paths stay legal. It also covers
 * nested routes for free: `/evaluations/:id` renders its children through
 * Evaluations_ShowCN's own <Outlet/>, so when this component renders the notice
 * instead of <Outlet/>, that screen never mounts and neither can its children.
 * Do not also wrap the children individually.
 *
 * Hiding a nav entry is not enough on its own — bookmarks, shared links and
 * emailed URLs all bypass the sidebar.
 */
const DesktopOnlyOutlet = () => {
	const isMobile = useIsMobile()
	const { pathname } = useLocation()

	/**
	 * Latch the decision per navigation.
	 *
	 * `useIsMobile` is reactive, so without this, dragging a desktop window
	 * narrower — or rotating a tablet from 1024 landscape to 768 portrait —
	 * would unmount whatever the user was working on mid-edit and replace it
	 * with the notice. This gate exists to stop deep links, not to police live
	 * resizing, so the verdict is taken once per path and held until the user
	 * navigates somewhere else.
	 */
	const latch = useRef<{ pathname: string; blocked: boolean } | null>(null)
	if (latch.current === null || latch.current.pathname !== pathname) {
		latch.current = { pathname, blocked: isMobile }
	}

	return latch.current.blocked ? <NotAvailableOnMobile /> : <Outlet />
}

export default DesktopOnlyOutlet
